import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, X, ZoomIn, ZoomOut } from 'lucide-react';
import { getAssetUrl, getRawAssetUrl } from '../../Api';
import { Foto } from '../Sustainability/comum';
import * as S from './style';

// Miniaturas montadas de cada lado da foto atual. O álbum pode ter mais de mil
// fotos: o trilho só existe perto de onde o visitante está.
const RAIO = 40;

// Foto ampliada em tela cheia: setas, Home/End, Esc, arrastar no celular e
// zoom. É um <dialog> nativo, que já prende o foco e fecha no Esc.
export const Visualizador = ({ titulo, fotos, inicio, ajuda, aoFechar }) => {
  const [atual, setAtual] = useState(inicio);
  const [zoom, setZoom] = useState(false);
  const dialogo = useRef(null);
  const trilho = useRef(null);
  const toqueX = useRef(0);
  const total = fotos.length;

  const ir = (i) => {
    setAtual(((i % total) + total) % total);
    setZoom(false);
  };

  useEffect(() => {
    const d = dialogo.current;
    d.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // A miniatura atual fica no meio do trilho.
  useEffect(() => {
    const t = trilho.current;
    const m = t?.querySelector('[aria-current="true"]');
    if (!m) return;
    const reduz = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    t.scrollTo({ left: m.offsetLeft - t.clientWidth / 2 + m.clientWidth / 2, behavior: reduz ? 'auto' : 'smooth' });
  }, [atual]);

  const teclado = (e) => {
    const acoes = {
      ArrowRight: () => ir(atual + 1),
      ArrowLeft: () => ir(atual - 1),
      Home: () => ir(0),
      End: () => ir(total - 1),
    };
    if (acoes[e.key]) {
      e.preventDefault();
      acoes[e.key]();
    }
  };

  const de = Math.max(0, atual - RAIO);
  const miniaturas = fotos.slice(de, atual + RAIO + 1);

  return (
    <S.Visualizador
      ref={dialogo}
      aria-label={titulo}
      onKeyDown={teclado}
      onClose={() => aoFechar(atual)}
      onClick={(e) => e.target === dialogo.current && dialogo.current.close()}
    >
      <S.VisTopo>
        <div>
          <span>{titulo}</span>
          <span aria-live="polite">
            {atual + 1} / {total}
          </span>
        </div>
        <div>
          {/* O original, sem redimensionar; "download=1" faz o Squidex mandar
              como anexo, e o navegador baixa em vez de abrir. */}
          <a href={`${getRawAssetUrl(fotos[atual])}?download=1`} aria-label="Baixar foto" download>
            <Download size={18} />
          </a>
          <button type="button" aria-pressed={zoom} aria-label="Ampliar" onClick={() => setZoom(!zoom)}>
            {zoom ? <ZoomOut size={18} /> : <ZoomIn size={18} />}
          </button>
          <button type="button" aria-label="Fechar" onClick={() => dialogo.current.close()}>
            <X size={20} />
          </button>
        </div>
      </S.VisTopo>

      <S.Palco
        $zoom={zoom}
        onTouchStart={(e) => {
          toqueX.current = e.changedTouches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (zoom) return;
          const dx = e.changedTouches[0].clientX - toqueX.current;
          if (Math.abs(dx) > 55) ir(atual + (dx < 0 ? 1 : -1));
        }}
      >
        <button type="button" aria-label="Foto anterior" onClick={() => ir(atual - 1)}>
          <ChevronLeft size={22} />
        </button>
        {/* O Squidex gera a versão grande na hora, e com foto original pesada isso
            leva segundos. Até ela chegar, aparece por baixo a mesma foto no tamanho
            da grade, que já está em cache: nunca fica um quadro vazio. */}
        <Foto
          key={fotos[atual]}
          id={fotos[atual]}
          largura={1600}
          alt={`${titulo} — ${atual + 1}`}
          loading="eager"
          style={{ backgroundImage: `url(${getAssetUrl(fotos[atual], { width: 480, format: 'webp' })})` }}
        />
        <button type="button" aria-label="Próxima foto" onClick={() => ir(atual + 1)}>
          <ChevronRight size={22} />
        </button>
      </S.Palco>

      <S.Miniaturas ref={trilho}>
        {miniaturas.map((id, i) => {
          const indice = de + i;
          return (
            <button
              type="button"
              key={`${id}-${indice}`}
              aria-current={indice === atual ? 'true' : 'false'}
              aria-label={`${indice + 1} / ${total}`}
              onClick={() => ir(indice)}
            >
              <Foto id={id} largura={160} alt="" />
            </button>
          );
        })}
      </S.Miniaturas>

      {ajuda && <S.Ajuda>{ajuda}</S.Ajuda>}
    </S.Visualizador>
  );
};
